import type { DistortionPoint } from "shared/schema/src/types";

/**
 * Apply vacuum effect (black hole compression) to image data
 * Creates a shrinking/compression effect around control points
 * @param imageData - ImageData to modify
 * @param vacuumPoints - Array of vacuum effect control points
 * @param power - Falloff power for vacuum effect (default: 1.0)
 * @param range - Range of vacuum effect in pixels (default: 100)
 * @returns New ImageData with vacuum effect applied
 */
export function applyVacuumEffectToImageData(
	imageData: ImageData,
	vacuumPoints: Array<{
		original: DistortionPoint;
		current: DistortionPoint;
	}>,
	power: number,
	range: number,
): ImageData {
	if (vacuumPoints.length === 0) return imageData;

	const width = imageData.width;
	const height = imageData.height;
	const originalData = imageData.data;

	// Create a mesh grid for distortion
	const gridSize = 20;
	const cols = gridSize;
	const rows = gridSize;
	const meshVertices: Array<{ x: number; y: number }> = [];

	// Initialize mesh vertices in a regular grid
	for (let row = 0; row <= rows; row++) {
		for (let col = 0; col <= cols; col++) {
			meshVertices.push({
				x: (col / cols) * width,
				y: (row / rows) * height,
			});
		}
	}

	// Apply vacuum effect to each vertex
	for (const point of vacuumPoints) {
		const centerX = point.current.x;
		const centerY = point.current.y;

		for (const vertex of meshVertices) {
			const distance = Math.sqrt(
				(vertex.x - centerX) ** 2 + (vertex.y - centerY) ** 2,
			);

			if (distance < range) {
				const falloff = (1 - distance / range) ** power;

				// Vacuum effect: shrink area at the center
				const angle = Math.atan2(vertex.y - centerY, vertex.x - centerX);
				const pinchStrength = falloff; // 0 = no pinch, 1 = max pinch
				const newDistance = distance * (1 + pinchStrength * 0.5); // Push outward for shrinking
				vertex.x = centerX + Math.cos(angle) * newDistance;
				vertex.y = centerY + Math.sin(angle) * newDistance;
			}
		}
	}

	// Create output image data
	const output = new ImageData(width, height);
	const outPixels = output.data;

	// For each pixel in the output, sample from the deformed mesh
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			// Find which grid cell this pixel belongs to
			const gridX = Math.floor((x / width) * cols);
			const gridY = Math.floor((y / height) * rows);

			// Clamp to valid range
			const clampedGridX = Math.max(0, Math.min(cols - 1, gridX));
			const clampedGridY = Math.max(0, Math.min(rows - 1, gridY));

			// Get the four corner vertices of this grid cell
			const topLeft = meshVertices[clampedGridY * (cols + 1) + clampedGridX];
			const topRight =
				meshVertices[clampedGridY * (cols + 1) + clampedGridX + 1];
			const bottomLeft =
				meshVertices[(clampedGridY + 1) * (cols + 1) + clampedGridX];
			const bottomRight =
				meshVertices[(clampedGridY + 1) * (cols + 1) + clampedGridX + 1];

			// Check if all vertices are valid
			if (topLeft && topRight && bottomLeft && bottomRight) {
				// Calculate local coordinates within the grid cell
				const localX = (x / width) * cols - clampedGridX;
				const localY = (y / height) * rows - clampedGridY;

				// Bilinear interpolation to find source position
				const sourceX =
					topLeft.x * (1 - localX) * (1 - localY) +
					topRight.x * localX * (1 - localY) +
					bottomLeft.x * (1 - localX) * localY +
					bottomRight.x * localX * localY;

				const sourceY =
					topLeft.y * (1 - localX) * (1 - localY) +
					topRight.y * localX * (1 - localY) +
					bottomLeft.y * (1 - localX) * localY +
					bottomRight.y * localX * localY;

				// Clamp source coordinates
				const clampedSourceX = Math.max(
					0,
					Math.min(width - 1, Math.round(sourceX)),
				);
				const clampedSourceY = Math.max(
					0,
					Math.min(height - 1, Math.round(sourceY)),
				);

				// Sample pixel from source position
				const sourceIndex = (clampedSourceY * width + clampedSourceX) * 4;
				const destIndex = (y * width + x) * 4;

				outPixels[destIndex] = originalData[sourceIndex] || 0; // R
				outPixels[destIndex + 1] = originalData[sourceIndex + 1] || 0; // G
				outPixels[destIndex + 2] = originalData[sourceIndex + 2] || 0; // B
				outPixels[destIndex + 3] = originalData[sourceIndex + 3] || 255; // A
			}
		}
	}

	return output;
}
